import { dispatchPromise } from './AppUtils';
import { claimPendingInfluencer as claimPendingInfluencerAction } from '../redux/buzzbridge/actions';

const STORAGE_KEY = 'thspian_influencer_prelink';
/** After guest IG OAuth when API returns `account_exists: true` — auto `requestToJoinCampaign` once user signs in on this campaign page */
export const PENDING_BUZZBRIDGE_APPLY_CAMPAIGN_KEY = 'thspian_buzzbridge_pending_apply_campaign_id';

export type BuzzGuestPlatformId = 'instagram' | 'tiktok' | 'youtube';

/** Platforms the user has successfully connected via OAuth in this session (guest or logged-in UI). */
export type InfluencerOAuthConnectedMap = Partial<Record<BuzzGuestPlatformId, { label?: string }>>;

export interface InfluencerPrelinkStored {
    influencer_id: string;
    claim_token: string;
}

export interface InfluencerPrelinkResponse {
    influencer_id: string;
    /** Absent or null when `account_exists` — no claim step */
    claim_token: string | null;
    linked?: boolean;
    /** True: Instagram already tied to a user — skip signup, sign in, then apply */
    account_exists?: boolean;
    /** Optional usernames returned by server for UI */
    connectedHint?: Partial<Record<BuzzGuestPlatformId, string>>;
}

export function setPendingBuzzBridgeApplyCampaignId(campaignId: string): void {
    try {
        sessionStorage.setItem(PENDING_BUZZBRIDGE_APPLY_CAMPAIGN_KEY, campaignId);
    } catch {
        // ignore
    }
}

export function getPendingBuzzBridgeApplyCampaignId(): string | null {
    try {
        return sessionStorage.getItem(PENDING_BUZZBRIDGE_APPLY_CAMPAIGN_KEY);
    } catch {
        return null;
    }
}

export function clearPendingBuzzBridgeApplyCampaignId(): void {
    try {
        sessionStorage.removeItem(PENDING_BUZZBRIDGE_APPLY_CAMPAIGN_KEY);
    } catch {
        // ignore
    }
}

export function saveInfluencerPrelinkToSession(data: InfluencerPrelinkStored): void {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
        // ignore quota / private mode
    }
}

export function loadInfluencerPrelinkFromSession(): InfluencerPrelinkStored | null {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as InfluencerPrelinkStored;
        if (parsed?.influencer_id && parsed?.claim_token) return parsed;
    } catch {
        // ignore
    }
    return null;
}

export function clearInfluencerPrelinkSession(): void {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // ignore
    }
}

function extractConnectedHint(d: Record<string, unknown>): Partial<Record<BuzzGuestPlatformId, string>> {
    const hint: Partial<Record<BuzzGuestPlatformId, string>> = {};
    (['instagram', 'tiktok', 'youtube'] as BuzzGuestPlatformId[]).forEach(key => {
        const block = d[key] as { username?: string } | undefined;
        const nested = (d.social_connections as Record<string, { username?: string }> | undefined)?.[key];
        const u = block?.username ?? nested?.username;
        if (u) hint[key] = String(u);
    });
    return hint;
}

export function parseInfluencerPrelinkResponse(res: unknown): InfluencerPrelinkResponse {
    const r = res as { data?: Record<string, unknown> } & Record<string, unknown>;
    let d = (r?.data ?? r) as Record<string, unknown>;
    if (d && typeof d === 'object' && !('influencer_id' in d) && d.data && typeof d.data === 'object') {
        d = d.data as Record<string, unknown>;
    }
    const influencer_id = d?.influencer_id as string | undefined;
    const account_exists = Boolean(d?.account_exists);
    const claimRaw = d?.claim_token;
    const claim_token =
        claimRaw === null || claimRaw === undefined
            ? null
            : typeof claimRaw === 'string' && claimRaw.length > 0
            ? claimRaw
            : null;

    if (!influencer_id) {
        throw new Error('Invalid influencer OAuth link response');
    }

    if (account_exists) {
        return {
            influencer_id,
            claim_token: null,
            linked: d.linked as boolean | undefined,
            account_exists: true,
            connectedHint: extractConnectedHint(d),
        };
    }

    if (!claim_token) {
        throw new Error('Invalid influencer OAuth link response');
    }

    return {
        influencer_id,
        claim_token,
        linked: d.linked as boolean | undefined,
        account_exists: false,
        connectedHint: extractConnectedHint(d),
    };
}

export type GuestOAuthLinkPayload = {
    code: string;
    provider: string;
    campaign_id?: string;
    /** Send on subsequent platform connects so the server merges into the same guest influencer */
    influencer_id?: string;
    /** Required alongside influencer_id for subsequent platform links */
    claim_token?: string;
};

/** @deprecated Prefer actions from redux/buzzbridge/actions via useDispatchPromise / dispatchPromise. */
export {
    postInfluencerGuestOAuthLink,
    postInfluencerOAuthConnect,
    postInfluencerFromConnectedInstagram,
    claimPendingInfluencer,
} from '../redux/buzzbridge/actions';

/** Thin wrapper so non-React callers (login flows / user saga) can await claim. */
export function claimPendingInfluencerIfAny(): Promise<boolean> {
    return dispatchPromise(claimPendingInfluencerAction()) as Promise<boolean>;
}
